/**
 * File: AgentJobsContext.tsx
 * Description: App-root, navigation-resilient tracker for long-running agent
 *   jobs. The provider is mounted above the router so it never unmounts on
 *   navigation; it owns a single poll loop per job (reusing `pollJob`),
 *   mirrors state to localStorage so runs survive a full reload, and emits a
 *   window event when a job finishes so pages can apply the result.
 */

import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from 'react';

import {
  AgentJobMap,
  loadAll,
  pruneTerminalOlderThan,
  remove as removeFromStore,
  upsert as upsertToStore,
} from '../services/agentJobsStore';
import { AGENT_JOB_COMPLETED_EVENT } from '../services/agentJobTargets';
import { pollJob } from '../services/jobPollingService';
import { AgentJobDescriptor, isTerminalStatus } from '../types/agentJob';
import { AsyncJobStartResponse } from '../types/asyncJob';

/** Retain finished jobs for 5 minutes so a returning page can read results. */
const TERMINAL_RETENTION_MINUTES = 5;
const TERMINAL_RETENTION_MS = TERMINAL_RETENTION_MINUTES * 60 * 1000;

/** Fallback error message when a job fails without a specific error. */
const AGENT_JOB_FAILED_MESSAGE = 'Agent job failed';

/** Input to start tracking a new background job. */
export interface StartJobInput {
  projectId: number;
  jobType: string;
  target?: string;
  label: string;
  /** Issues the `POST .../async` request and resolves with the job id. */
  startFn: () => Promise<AsyncJobStartResponse>;
}

/**
 * Owns the set of tracked jobs and their poll loops. Exposed via context so it
 * is a stable singleton for the app and injectable in tests.
 */
export class AgentJobsManager {
  private snapshot: AgentJobMap;
  private readonly listeners = new Set<() => void>();
  private readonly controllers = new Map<string, AbortController>();

  constructor(initial?: AgentJobMap) {
    this.snapshot = initial ?? loadAll();
  }

  /** Subscribe to store changes (for `useSyncExternalStore`). */
  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /** Current immutable snapshot (stable reference until something changes). */
  getSnapshot = (): AgentJobMap => this.snapshot;

  /**
   * Rehydrate from storage and resume polling every non-terminal job. Called
   * once when the provider mounts.
   */
  resumeActive(): void {
    pruneTerminalOlderThan(TERMINAL_RETENTION_MS);
    this.commit(loadAll());
    for (const descriptor of Object.values(this.snapshot)) {
      if (!isTerminalStatus(descriptor.status)) {
        this.poll(descriptor);
      }
    }
  }

  /**
   * Start a new tracked job: issue the start request, persist the descriptor,
   * and begin polling. Resolves with the job id once the job has started.
   */
  async startJob(input: StartJobInput): Promise<string> {
    const start = await input.startFn();
    const descriptor: AgentJobDescriptor = {
      jobId: start.job_id,
      projectId: input.projectId,
      jobType: input.jobType || start.job_type,
      target: input.target,
      label: input.label,
      startedAt: Date.now(),
      status: 'running',
    };
    this.set(descriptor);
    this.poll(descriptor);
    return descriptor.jobId;
  }

  /**
   * Start a tracked job and resolve once it finishes. The poll is owned by the
   * tracker (so the work is followed to completion even if the caller's
   * component unmounts); the returned promise simply mirrors the terminal
   * outcome for callers that stay mounted.
   */
  async startJobAndWait<TResult = unknown>(
    input: StartJobInput,
  ): Promise<TResult> {
    const jobId = await this.startJob(input);
    return this.waitFor<TResult>(jobId);
  }

  /** Resolve (succeeded) or reject (failed) when a tracked job goes terminal. */
  waitFor<TResult = unknown>(jobId: string): Promise<TResult> {
    const current = this.snapshot[jobId];
    if (current && isTerminalStatus(current.status)) {
      return current.status === 'succeeded'
        ? Promise.resolve(current.result as TResult)
        : Promise.reject(new Error(current.error ?? AGENT_JOB_FAILED_MESSAGE));
    }
    return new Promise<TResult>((resolve, reject) => {
      const handler = (event: Event): void => {
        const descriptor = (event as CustomEvent<AgentJobDescriptor>).detail;
        if (descriptor?.jobId !== jobId) { return; }
        globalThis.removeEventListener(AGENT_JOB_COMPLETED_EVENT, handler);
        if (descriptor.status === 'succeeded') {
          resolve(descriptor.result as TResult);
        } else {
          reject(new Error(descriptor.error ?? AGENT_JOB_FAILED_MESSAGE));
        }
      };
      globalThis.addEventListener(AGENT_JOB_COMPLETED_EVENT, handler);
    });
  }

  /** Stop tracking a job locally (does NOT cancel the backend work). */
  cancelTracking(jobId: string): void {
    this.controllers.get(jobId)?.abort();
    this.controllers.delete(jobId);
    removeFromStore(jobId);
    const next = { ...this.snapshot };
    delete next[jobId];
    this.commit(next);
  }

  private emit(): void {
    this.listeners.forEach((listener) => listener());
  }

  private commit(next: AgentJobMap): void {
    this.snapshot = next;
    this.emit();
  }

  private set(descriptor: AgentJobDescriptor): void {
    upsertToStore(descriptor);
    this.commit({ ...this.snapshot, [descriptor.jobId]: descriptor });
  }

  private patch(jobId: string, patch: Partial<AgentJobDescriptor>): void {
    const existing = this.snapshot[jobId];
    if (!existing) { return; }
    const merged = { ...existing, ...patch };
    upsertToStore(merged);
    this.commit({ ...this.snapshot, [jobId]: merged });
  }

  private poll(descriptor: AgentJobDescriptor): void {
    if (this.controllers.has(descriptor.jobId)) { return; } // already polling
    const controller = new AbortController();
    this.controllers.set(descriptor.jobId, controller);
    if (descriptor.status === 'pending') {
      this.patch(descriptor.jobId, { status: 'running' });
    }
    void this.drive(descriptor.jobId, descriptor.projectId, controller);
  }

  private async drive(
    jobId: string,
    projectId: number,
    controller: AbortController,
  ): Promise<void> {
    try {
      const result = await pollJob(projectId, jobId, {
        signal: controller.signal,
      });
      if (controller.signal.aborted) { return; }
      this.patch(jobId, {
        status: 'succeeded',
        result,
        finishedAt: Date.now(),
      });
      this.dispatchCompleted(jobId);
    } catch (error) {
      if (controller.signal.aborted) { return; }
      const message =
        error instanceof Error ? error.message : AGENT_JOB_FAILED_MESSAGE;
      this.patch(jobId, {
        status: 'failed',
        error: message,
        finishedAt: Date.now(),
      });
      this.dispatchCompleted(jobId);
    } finally {
      this.controllers.delete(jobId);
    }
  }

  private dispatchCompleted(jobId: string): void {
    const descriptor = this.snapshot[jobId];
    if (!descriptor || globalThis.window === undefined) { return; }
    globalThis.dispatchEvent(
      new CustomEvent<AgentJobDescriptor>(AGENT_JOB_COMPLETED_EVENT, {
        detail: descriptor,
      }),
    );
  }
}

const AgentJobsContext = createContext<AgentJobsManager | null>(null);

/** Access the tracker manager; throws when used outside the provider. */
export const useAgentJobsManager = (): AgentJobsManager => {
  const manager = useContext(AgentJobsContext);
  if (!manager) {
    throw new Error(
      'useAgentJobs hooks must be used within an <AgentJobsProvider>',
    );
  }
  return manager;
};

interface AgentJobsProviderProps {
  children: ReactNode;
  /** Optional pre-built manager (used by tests). */
  manager?: AgentJobsManager;
}

/**
 * Mount once at the app root, outside the router. Resumes any in-flight jobs
 * persisted from a previous session on mount.
 */
export const AgentJobsProvider: React.FC<AgentJobsProviderProps> = ({
  children,
  manager,
}) => {
  const [managerInstance] = useState<AgentJobsManager>(
    () => manager ?? new AgentJobsManager(),
  );

  useEffect(() => {
    managerInstance.resumeActive();
  }, [managerInstance]);

  return (
    <AgentJobsContext.Provider value={managerInstance}>
      {children}
    </AgentJobsContext.Provider>
  );
};
